"use client"

import * as React from "react"
import { forwardRef, useImperativeHandle, useRef } from "react"
import confetti from "canvas-confetti"
import { Button } from "./button"

// Define the type for the confetti function returned by create()
type ConfettiFunction = (options?: confetti.Options) => Promise<null> | null;

export type ConfettiOptions = confetti.Options

export interface ConfettiRef {
  fire: (options?: ConfettiOptions) => void
}

export const Confetti = forwardRef<
  ConfettiRef,
  React.HTMLAttributes<HTMLCanvasElement>
>(({ className, ...props }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const confettiRef = useRef<ConfettiFunction | null>(null)

  useImperativeHandle(ref, () => ({
    fire: (options = {}) => {
      if (!confettiRef.current) {
        if (!canvasRef.current) return

        // Create a confetti instance with the canvas
        const myConfetti = confetti.create(canvasRef.current, {
          resize: true,
          useWorker: true,
        })

        // Store the function that creates confetti
        confettiRef.current = myConfetti
      }

      // Call the confetti function with options
      confettiRef.current({
        spread: 60,
        startVelocity: 20,
        particleCount: 50,
        ...options,
      })
    },
  }))

  return <canvas ref={canvasRef} className={className} {...props} />
})
Confetti.displayName = "Confetti"

export interface ConfettiButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  options?: ConfettiOptions
}

export const ConfettiButton = forwardRef<HTMLButtonElement, ConfettiButtonProps>(
  ({ options, onClick, ...props }, ref) => {
    const confettiRef = useRef<ConfettiRef>(null)

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      confettiRef.current?.fire(options)
      onClick?.(e)
    }

    return (
      <>
        <Confetti
          ref={confettiRef}
          className="pointer-events-none fixed left-0 top-0 z-50 h-full w-full"
        />
        <Button ref={ref} onClick={handleClick} {...props} />
      </>
    )
  }
)
ConfettiButton.displayName = "ConfettiButton"
